import type { ControlledDialogProps } from "@/hooks/useDialogController.tsx";
import { useUpdateWebDirectCustomHomeUrlMutation } from "@/mutations/filemaker-server.ts";
import { useActiveClient } from "@/routes/$clientId.tsx";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { useSnackbar } from "notistack";
import { type ReactNode, useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
    urls: z.preprocess((value) => {
        if (typeof value !== "string") {
            return value;
        }

        return value
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line !== "");
    }, z.array(z.string().url())),
});

type FieldValues = z.input<typeof schema>;
type TransformedValues = z.output<typeof schema>;

type Props = {
    hostId: string;
    urls: string[];
    dialogProps: ControlledDialogProps;
};

const ChangeWebDirectCustomHomeUrlsDialog = ({ hostId, urls, dialogProps }: Props): ReactNode => {
    const { id: clientId } = useActiveClient();
    const mutation = useUpdateWebDirectCustomHomeUrlMutation(clientId);
    const form = useForm<FieldValues, unknown, TransformedValues>({
        resolver: zodResolver(schema),
        defaultValues: { urls: urls.join("\n") },
    });
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = useCallback(
        (values: TransformedValues) => {
            mutation.mutate(
                {
                    hostId,
                    ...values,
                },
                {
                    onSuccess: (message) => {
                        enqueueSnackbar(message, { variant: "success" });
                        dialogProps.onClose();
                    },
                    onError: (error) => {
                        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
                    },
                },
            );
        },
        [enqueueSnackbar, hostId, mutation, dialogProps.onClose],
    );

    return (
        <Dialog
            {...dialogProps}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                component: "form",
                noValidate: true,
                onSubmit: form.handleSubmit(handleSubmit),
            }}
        >
            <DialogTitle>WebDirect custom home URL</DialogTitle>
            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    To enable WebDirect custom home URLs, supply the URLs in the format
                    &quot;https://&lt;domain&gt;/your_page.html&quot;, do not include a trailing
                    slash. For multiple URLs put each URL on its own line. To disable, clear the
                    URLs. Upon submission the Web Publishing engine will restart and disconnect any
                    WebDirect clients.
                </DialogContentText>
                <RhfTextField
                    control={form.control}
                    name="urls"
                    label="URLs"
                    multiline
                    minRows={3}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={dialogProps.onClose}>
                    Cancel
                </Button>
                <LoadingButton loading={mutation.isPending} type="submit">
                    Update
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeWebDirectCustomHomeUrlsDialog;
